
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { module1Service, module2Service, module3Service, module4Service } from '../services';
import { debugLog } from '../utils/debug';
import { 
  loadWorkflowState, 
  saveWorkflowState, 
  clearWorkflowState, 
  WorkflowState,
  ApprovalHistoryEntry
} from '../utils/workflow';
import { BusinessConfig } from '../services/businessConfigService';

interface ServiceStatus {
  module1: boolean;
  module2: boolean;
  module3: boolean;
  module4: boolean;
}

interface ServiceHealthDetail {
  status: boolean;
  message?: string;
  lastChecked?: Date;
  responseTime?: number;
  details?: any;
  error?: Error;
}

interface ServiceHealthDetails {
  module1: ServiceHealthDetail;
  module2: ServiceHealthDetail;
  module3: ServiceHealthDetail;
  module4: ServiceHealthDetail;
}

interface AppContextType {
  // Service information
  serviceStatus: ServiceStatus;
  serviceHealthDetails: ServiceHealthDetails;
  isLoading: boolean;
  
  // Workflow state
  workflowId: string | null;
  setWorkflowId: (id: string | null) => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  workflowData: WorkflowState['workflowData'];
  updateWorkflowData: (data: Partial<WorkflowState['workflowData']>) => void;
  
  // Approval management
  approvalHistory: ApprovalHistoryEntry[];
  addApprovalEntry: (entry: Omit<ApprovalHistoryEntry, 'timestamp'>) => void;
  
 // Business configuration
  businessConfig: BusinessConfig | null;
  setBusinessConfig: (config: BusinessConfig | null) => void;
  
  // Actions
  resetWorkflow: () => void;
  checkServicesStatus: () => Promise<void>;
  persistWorkflowState: () => void;
  
  // UI state
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

const initialServiceHealth: ServiceHealthDetail = {
  status: false,
  message: 'Not checked yet',
  lastChecked: undefined,
  responseTime: undefined
};

const AppContext = createContext<AppContextType | undefined>(undefined);

// Add these constants at the top of the file
const HEALTH_CHECK_TIMEOUT = 5000; // 5 seconds
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Add this utility function
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const AppProvider = ({ children }: { children: ReactNode }) => {
  // Load initial state from localStorage (if exists)
  const persistedState = loadWorkflowState();
  
  // Service status
  const [serviceStatus, setServiceStatus] = useState<ServiceStatus>({
    module1: false,
    module2: false,
    module3: false,
    module4: false,
  });
  
  const [serviceHealthDetails, setServiceHealthDetails] = useState<ServiceHealthDetails>({
    module1: { ...initialServiceHealth },
    module2: { ...initialServiceHealth },
    module3: { ...initialServiceHealth },
    module4: { ...initialServiceHealth },
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Workflow state (initialized from persisted state)
  const [workflowId, setWorkflowId] = useState<string | null>(persistedState.workflowId);
  const [currentStep, setCurrentStep] = useState(persistedState.currentStep);
  const [workflowData, setWorkflowData] = useState<WorkflowState['workflowData']>(
    persistedState.workflowData || {}
  );
  const [approvalHistory, setApprovalHistory] = useState<ApprovalHistoryEntry[]>(
    persistedState.approvalHistory || []
  );

  // Business configuration
  const [businessConfig, setBusinessConfig] = useState<BusinessConfig | null>(null);

  // Persist workflow state to localStorage
  const persistWorkflowState = useCallback((
    id = workflowId, 
    step = currentStep, 
    data = workflowData, 
    history = approvalHistory
  ) => {
    const state: WorkflowState = {
      workflowId: id,
      currentStep: step,
      workflowData: data,
      approvalHistory: history
    };
    saveWorkflowState(state);
  }, [workflowId, currentStep, workflowData, approvalHistory]);

  // Update workflow data and persist it
  const updateWorkflowData = useCallback((data: Partial<WorkflowState['workflowData']>) => {
    setWorkflowData(prevData => {
      const newData = { ...prevData, ...data };
      // Persist state on significant updates
      persistWorkflowState(workflowId, currentStep, newData, approvalHistory);
      return newData;
    });
  }, [workflowId, currentStep, approvalHistory, persistWorkflowState]);
  
  // Add approval entry to history
  const addApprovalEntry = useCallback((entry: Omit<ApprovalHistoryEntry, 'timestamp'>) => {
    const newEntry: ApprovalHistoryEntry = {
      ...entry,
      timestamp: new Date().toISOString()
    };
    
    setApprovalHistory(prev => {
      const newHistory = [...prev, newEntry];
      // Persist state when approval entries are added
      persistWorkflowState(workflowId, currentStep, workflowData, newHistory);
      return newHistory;
    });
  }, [workflowId, currentStep, workflowData, persistWorkflowState]);

  // Reset workflow and clear localStorage
  const resetWorkflow = useCallback(() => {
    clearWorkflowState();
    setWorkflowId(null);
    setCurrentStep(0);
    setWorkflowData({});
    setApprovalHistory([]);
  }, []);

  // Update workflow ID and persist state
  useEffect(() => {
    if (workflowId !== persistedState.workflowId) {
      persistWorkflowState();
    }
  }, [workflowId, persistedState.workflowId, persistWorkflowState]);
  
  // Update step and persist state
  useEffect(() => {
    if (currentStep !== persistedState.currentStep) {
      persistWorkflowState();
    }
  }, [currentStep, persistedState.currentStep, persistWorkflowState]);

  // Load saved workflow ID and business config from localStorage
  useEffect(() => {
    const savedWorkflowId = localStorage.getItem('workflowId');
    const savedBusinessConfig = localStorage.getItem('businessConfig');
    
    if (savedWorkflowId) {
      setWorkflowId(savedWorkflowId);
    }
    
    if (savedBusinessConfig) {
      try {
        setBusinessConfig(JSON.parse(savedBusinessConfig));
      } catch (error) {
        console.error('Error parsing saved business config:', error);
      }
    }
  }, []);

  // Save workflow ID and business config to localStorage when they change
  useEffect(() => {
    if (workflowId) {
      localStorage.setItem('workflowId', workflowId);
    } else {
      localStorage.removeItem('workflowId');
    }
  }, [workflowId]);

  useEffect(() => {
    if (businessConfig) {
      localStorage.setItem('businessConfig', JSON.stringify(businessConfig));
    } else {
      localStorage.removeItem('businessConfig');
    }
  }, [businessConfig]);

  // Check a single service health
  const checkServiceHealth = async (
    service: any,
    serviceName: keyof ServiceStatus
  ): Promise<ServiceHealthDetail> => {
    try {
      // Mock mode override for local development - set to true for mock responses
      // You can make this use a config or environment variable if needed
      const useMockMode = false; 
      
      if (useMockMode) {
        // In mock mode, return a healthy status
        debugLog('AppContext', `${serviceName} health check mocked as healthy`);
        return { 
          status: true, 
          message: 'Healthy (mocked)',
          details: { 
            status: 'healthy',
            timestamp: new Date().toISOString() 
          }
        };
      }
      
      // Check if service is a class with getInstance method or already an instance
      const serviceInstance = typeof service.getInstance === 'function' 
        ? service.getInstance() 
        : service;
      
      // Check if getHealth exists before calling it
      if (typeof serviceInstance.getHealth !== 'function') {
        debugLog('AppContext', `${serviceName} doesn't have a getHealth method, creating mock response`);
        return {
          status: true,
          message: 'Healthy (mocked)',
          details: {
            status: 'healthy',
            timestamp: new Date().toISOString()
          }
        };
      }
      
      // With timeout for quick failure if the service is unreachable
      const result = await Promise.race([
        serviceInstance.getHealth(),
        new Promise<any>((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), 3000)
        )
      ]);
      
      const isHealthy = 
        result && 
        (result.status === 'healthy' || 
         result.status === 'OK' || 
         result.status === 'ok');
      
      debugLog('AppContext', `${serviceName} health check result:`, result);
      
      return {
        status: isHealthy,
        message: isHealthy ? 'Healthy' : 'Unhealthy',
        details: result
      };
    } catch (error) {
      // Handle errors (service unreachable, etc.)
      debugLog('AppContext', `${serviceName} health check failed:`, error);
      return {
        status: false,
        message: error instanceof Error ? error.message : 'Unknown error',
        error: error as Error
      };
    }
  };

  // Update the checkServicesStatus function
  const checkServicesStatus = async () => {
    setIsLoading(true);
    
    debugLog('AppContext', 'Checking services status...');
    
    try {
      // Run health checks in parallel with individual timeouts
      const [
        module1Health,
        module2Health,
        module3Health,
        module4Health
      ] = await Promise.all([
        checkServiceHealth(module1Service, 'module1'),
        checkServiceHealth(module2Service, 'module2'),
        checkServiceHealth(module3Service, 'module3'),
        checkServiceHealth(module4Service, 'module4')
      ]);

      // Update detailed health info
      setServiceHealthDetails({
        module1: module1Health,
        module2: module2Health,
        module3: module3Health,
        module4: module4Health
      });
      
      // Update simple status for backward compatibility
      setServiceStatus({
        module1: module1Health.status,
        module2: module2Health.status,
        module3: module3Health.status,
        module4: module4Health.status,
      });
      
      debugLog('AppContext', 'Services status updated', {
        module1: module1Health.status ? 'UP' : 'DOWN',
        module2: module2Health.status ? 'UP' : 'DOWN',
        module3: module3Health.status ? 'UP' : 'DOWN',
        module4: module4Health.status ? 'UP' : 'DOWN',
      });
    } catch (error) {
      console.error('Error checking service status:', error);
      // Set all services to down if there's a critical error
      setServiceStatus({
        module1: false,
        module2: false,
        module3: false,
        module4: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // DISABLED: Health check polling to reduce console noise
    // User is actively using the app, no need for continuous health checks
    
    // Check service status on initial load only
    // checkServicesStatus();
    
    // DISABLED: Periodic health checks
    // const interval = setInterval(() => {
    //   checkServicesStatus();
    // }, 60000);

    // return () => clearInterval(interval);
  }, []);

  return (
    <AppContext.Provider
      value={{
        serviceStatus,
        serviceHealthDetails,
        isLoading,
        workflowId,
        setWorkflowId,
        currentStep,
        setCurrentStep,
        workflowData,
        updateWorkflowData,
        approvalHistory,
        addApprovalEntry,
        businessConfig,
        setBusinessConfig,
        resetWorkflow,
        checkServicesStatus,
        persistWorkflowState,
        setLoading: setIsLoading,
        setError,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};